import React from 'react';
import Helmet from 'react-helmet';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { LinearProgress, Grid } from '@material-ui/core';
import { reduxForm } from 'redux-form/immutable';
import { compose } from 'redux';

import PageHeaderRedesign from 'retired/PageHeaderRedesign';
import Box from 'retired/Box';
import { selectUser } from 'entities/user/selectors';
import { AffiliationsListDocument, useCreateEmployeeAffiliationMutation } from 'generated/graphql';

import { submitAction, submitFailedAction, submitSuccessAction } from './actions';
import { ADD_AFFILIATION_FORM_IDENTIFIER } from './constants';
import { selectLoading } from './selectors';
import { getAffiliationEnum } from './getAffiliationEnum';
import { getBoardPositionEnum } from './getBoardPositionEnum';
import { AffiliationsForm } from './AffiliationsForm';
import { withInitialValues } from './withInitialValues';

const AddAffiliation = ({
  userId,
  loading: passedLoading,
  initialLocation,
  form,
  submitting,
}: {
  userId: string;
  loading: boolean;
  initialLocation: string;
  form: string;
  submitting: boolean;
}): JSX.Element => {
  const dispatch = useDispatch();
  const history = useHistory();
  const me = useSelector(selectUser);
  const reduxLoading = useSelector(selectLoading);

  const [createEmployeeAffiliationMutation, { loading: createLoading }] =
    useCreateEmployeeAffiliationMutation({
      onCompleted: (data) => {
        dispatch(submitSuccessAction(userId, data));
      },
      onError: (error) => {
        dispatch(submitFailedAction(error));
      },
    });
  const loading: boolean = passedLoading || reduxLoading || createLoading;

  const submit = (formData: Record<string, unknown>): void => {
    dispatch(submitAction(userId, formData));
    const updateData = formData.toObject();
    const nonprofit = updateData.nonprofits.toJS()[0];

    createEmployeeAffiliationMutation({
      variables: {
        data: {
          userId,
          companyId: me?.organization.id,
          nonprofitId: nonprofit.id,
          affiliationType: getAffiliationEnum(updateData.affiliationType),
          boardPosition: getBoardPositionEnum(updateData.boardPosition),
          startingAt: updateData.startingAt,
          endingAt: updateData.endingAt,
        },
      },
      refetchQueries: [
        {
          query: AffiliationsListDocument,
          variables: { userId },
        },
      ],
    });
  };

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <Grid container>
      <Grid item xs={12}>
        <Helmet title="Add Employee Affiliations to Nonprofits" />
        <PageHeaderRedesign
          title="Add Employee Affiliations to Nonprofits"
          onArrowClick={(): void =>
            history.length > 0 ? history.goBack() : history.push(`/volunteers/${userId}`)
          }
        />
      </Grid>
      <Grid item xs={9}>
        <AffiliationsForm
          initialLocation={initialLocation}
          submit={submit}
          submitting={submitting || loading}
          form={form}
        />
      </Grid>
      <Grid item md={3}>
        <Box
          style={{
            backgroundColor: 'unset',
          }}
        >
          Add Nonprofit Affiliations on behalf of this employee. Affiliations will be visible to the
          employee on their profile where they will be able to make updates
        </Box>
      </Grid>
    </Grid>
  );
};

const withForm = reduxForm({
  form: ADD_AFFILIATION_FORM_IDENTIFIER,
  enableReinitialize: true,
});

export default compose(withInitialValues, withForm)(AddAffiliation);
